import { ArrowRight, PauseCircle } from 'lucide-react';

import { SalesTableState } from '@/components/sales/SalesTableState';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useZeroAumHolds } from '@/hooks/useRenewals';
import type { RenewalReminderKind } from '@/services/renewals.service';
import { formatDate, formatDateTime } from '@/utils/format';

const REMINDER_LABEL: Record<RenewalReminderKind, string> = {
  notice: 'Renewal Notice',
  followup1: 'Follow-up 1',
  followup2: 'Follow-up 2',
  termination: 'Termination',
};

interface ZeroAumHoldsTabProps {
  onOpen: (id: number) => void;
}

// AUM-based renewals whose live AUM (AMFI) is exactly 0. Their automated
// reminders are held, not sent; each one is re-checked against the live AUM
// and goes out once the AUM is non-zero again.
export const ZeroAumHoldsTab = ({ onOpen }: ZeroAumHoldsTabProps) => {
  const { data, isLoading, isError, refetch } = useZeroAumHolds();
  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <PauseCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        Clients whose AUM fetched live from AMFI is 0. Their reminder emails are on hold and are
        re-checked automatically - they resume once the AUM is above 0.
      </p>

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1">
        {isLoading && (
          <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
            Loading zero-AUM clients…
          </div>
        )}
        {!isLoading && isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load zero-AUM clients"
            description="Something went wrong while fetching them. Please try again."
            onRetry={() => {
              void refetch();
            }}
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <SalesTableState
            variant="empty"
            title="No reminders on hold"
            description="A renewal appears here when its client's live AUM is 0 and reminders are still pending."
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Renewal</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>ARN</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Held Since</TableHead>
                  <TableHead>Pending Reminders</TableHead>
                  <TableHead>Next AUM Check</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => onOpen(r.id)}
                  >
                    <TableCell className="font-mono text-xs font-semibold">{r.renewalCode}</TableCell>
                    <TableCell className="font-semibold">{r.clientName}</TableCell>
                    <TableCell>{r.arn}</TableCell>
                    <TableCell>{r.productName}</TableCell>
                    <TableCell>{formatDate(r.dueDate)}</TableCell>
                    <TableCell>{formatDateTime(r.heldSince)}</TableCell>
                    <TableCell>
                      <p className="font-semibold">{r.pendingReminders}</p>
                      <p className="text-xs text-muted-foreground">
                        {`Next: ${REMINDER_LABEL[r.nextReminderKind]}`}
                      </p>
                    </TableCell>
                    <TableCell>{formatDateTime(r.nextReminderAt)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(r.id);
                        }}
                      >
                        Open
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};
